import type { Brand } from '../brand/types';
import { Rng } from '../sim/rng';
import type { Tunables } from '../sim/tunables';

/**
 * Draws the whole field (both end zones included) onto one canvas.
 * Canvas x runs sideline to sideline (world x); canvas y runs from the far end line (top, world -z)
 * to the near end line (bottom, world +z), so text drawn upright reads correctly from behind the QB.
 */
export function drawFieldCanvas(brand: Brand, t: Tunables, logo: HTMLImageElement | null): HTMLCanvasElement {
  const f = t.field;
  const ppy = f.texturePxPerYard;
  const w = Math.round(f.widthYards * ppy);
  const h = Math.round(f.lengthYards * ppy);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas not available');

  const ez = f.endZoneYards * ppy;
  const line = Math.max(1, f.lineWidthYards * ppy);

  // Grass with mowing stripes, then wear where the play happens, then fine grain.
  ctx.fillStyle = t.look.grass;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = t.look.grassStripe;
  for (let y = 0, i = 0; y < f.lengthYards; y += f.stripeEveryYards, i++) {
    if (i % 2 === 1) ctx.fillRect(0, y * ppy, w, f.stripeEveryYards * ppy);
  }
  drawWear(ctx, w, h, ez, t);
  drawGrain(ctx, w, h, t);

  // End zones in the brand's primary colour, wordmark in the accent.
  ctx.fillStyle = brand.colors.primary;
  ctx.fillRect(0, 0, w, ez);
  ctx.fillRect(0, h - ez, w, ez);
  if (logo) {
    const tinted = tintImage(logo, brand.colors.accent);
    const maxH = ez * f.endZoneLogoHeightFraction;
    const maxW = w * f.endZoneLogoMaxWidthFraction;
    const scale = Math.min(maxH / tinted.height, maxW / tinted.width);
    const lw = tinted.width * scale;
    const lh = tinted.height * scale;
    // Far end zone: upright from the behind-QB camera.
    ctx.drawImage(tinted, (w - lw) / 2, (ez - lh) / 2, lw, lh);
    // Near end zone: turned 180 degrees so it reads from the other end.
    ctx.save();
    ctx.translate(w / 2, h - ez / 2);
    ctx.rotate(Math.PI);
    ctx.drawImage(tinted, -lw / 2, -lh / 2, lw, lh);
    ctx.restore();
  }

  ctx.fillStyle = brand.colors.fieldLine;
  // Boundary.
  ctx.fillRect(0, 0, w, line);
  ctx.fillRect(0, h - line, w, line);
  ctx.fillRect(0, 0, line, h);
  ctx.fillRect(w - line, 0, line, h);

  // Yard lines every 5, goal lines included.
  const playing = f.lengthYards - 2 * f.endZoneYards;
  for (let yd = 0; yd <= playing; yd += f.yardLineEveryYards) {
    const y = ez + yd * ppy;
    ctx.fillRect(0, y - line / 2, w, line);
  }

  // One-yard ticks: at both sidelines and on both hash lines.
  const hashLen = f.hashLengthYards * ppy;
  const cx = w / 2;
  const hashOff = f.hashFromCenterYards * ppy;
  for (let yd = 1; yd < playing; yd++) {
    if (yd % f.yardLineEveryYards === 0) continue;
    const y = ez + yd * ppy - line / 2;
    ctx.fillRect(line, y, hashLen, line);
    ctx.fillRect(w - line - hashLen, y, hashLen, line);
    ctx.fillRect(cx - hashOff - hashLen / 2, y, hashLen, line);
    ctx.fillRect(cx + hashOff - hashLen / 2, y, hashLen, line);
  }

  // Yard numbers, tops facing the middle of the field.
  const numH = f.numberHeightYards * ppy;
  ctx.font = `800 ${Math.round(numH)}px ${t.look.numberFont}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const arrow = f.numberArrowYards * ppy;
  for (let yd = f.numberEveryYards; yd < playing; yd += f.numberEveryYards) {
    const label = String(Math.min(yd, playing - yd));
    const y = ez + yd * ppy;
    const off = f.numberFromSidelineYards * ppy;
    drawRotated(ctx, label, off, y, Math.PI / 2);
    drawRotated(ctx, label, w - off, y, -Math.PI / 2);
    // Arrow beside each number, pointing to the nearer goal line (none at midfield).
    if (yd * 2 === playing) continue;
    const toFar = yd < playing / 2 ? -1 : 1;
    const ay = y + toFar * (numH * 0.95);
    for (const ax of [off, w - off]) {
      ctx.beginPath();
      ctx.moveTo(ax, ay + toFar * arrow);
      ctx.lineTo(ax - arrow * 0.8, ay);
      ctx.lineTo(ax + arrow * 0.8, ay);
      ctx.closePath();
      ctx.fill();
    }
  }

  return canvas;
}

/** Worn, browner patches: most in the middle of the field around the hashes, fading toward the sidelines. */
function drawWear(ctx: CanvasRenderingContext2D, w: number, h: number, ez: number, t: Tunables): void {
  const f = t.field;
  const ppy = f.texturePxPerYard;
  const rng = new Rng(7);
  ctx.save();
  ctx.fillStyle = t.look.grassWear;
  for (let i = 0; i < f.wearPatches; i++) {
    // Bias toward the centre: average of two uniforms.
    const x = w / 2 + (rng.next() + rng.next() - 1) * f.wearSpreadYards * ppy;
    const y = ez + rng.next() * (h - 2 * ez);
    const r = (1.5 + rng.next() * 4) * ppy;
    ctx.globalAlpha = f.wearMaxAlpha * (0.3 + 0.7 * rng.next()) * (1 - Math.abs(x - w / 2) / (w / 2));
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * (0.6 + rng.next() * 0.8), rng.next() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** Per-pixel brightness noise so the grass is not a flat fill. */
function drawGrain(ctx: CanvasRenderingContext2D, w: number, h: number, t: Tunables): void {
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const rng = new Rng(11);
  const k = t.field.grassNoise * 255;
  for (let i = 0; i < d.length; i += 4) {
    const n = (rng.next() - 0.5) * 2 * k;
    d[i] = d[i]! + n;
    d[i + 1] = d[i + 1]! + n;
    d[i + 2] = d[i + 2]! + n * 0.6;
  }
  ctx.putImageData(img, 0, 0);
}

/** A small tiling grass-blade texture, multiplied over the field up close. */
export function grassDetailCanvas(size: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2D canvas not available');
  const img = ctx.createImageData(size, size);
  const rng = new Rng(23);
  for (let i = 0; i < size * size; i++) {
    const v = 128 + (rng.next() - 0.5) * 120;
    img.data[i * 4] = v;
    img.data[i * 4 + 1] = v;
    img.data[i * 4 + 2] = v;
    img.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  // Short streaks along the mowing direction read as blades.
  ctx.globalAlpha = 0.25;
  for (let i = 0; i < size * 2; i++) {
    const x = rng.next() * size;
    const y = rng.next() * size;
    ctx.fillStyle = rng.next() < 0.5 ? 'black' : 'white';
    ctx.fillRect(x, y, 1, 2 + rng.next() * 4);
  }
  return c;
}

function drawRotated(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, angle: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.fillText(text, 0, 0);
  ctx.restore();
}

/** Recolours a single-colour logo image to the given colour, keeping its alpha. */
export function tintImage(img: HTMLImageElement, color: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = img.naturalWidth || img.width;
  c.height = img.naturalHeight || img.height;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2D canvas not available');
  ctx.drawImage(img, 0, 0, c.width, c.height);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, c.width, c.height);
  return c;
}
